import React, { useEffect, useState } from 'react';
import { X, Loader, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';

const money = (n) => `$${Number(n || 0).toFixed(2).replace(/\.00$/, '')}`;

/**
 * Asking to be paid what the platform is holding.
 *
 * Two steps in one place, because they are one decision to the host: how much,
 * and the billing details an invoice cannot legally omit. The details are asked
 * for once and reused; after the first payout this is a single field.
 */
export default function RequestPayoutModal({ ownerType, ownerId, owed, onClose, onCreated }) {
  const { theme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('full');
  const [amount, setAmount] = useState('');
  const [form, setForm] = useState({
    legalName: '', addressLine1: '', addressLine2: '',
    city: '', region: '', postalCode: '', country: '', taxId: '',
  });

  useEffect(() => {
    paymentsAPI.getBillingProfile(ownerType, ownerId)
      .then(({ profile: p }) => {
        if (p) { setProfile(p); setForm(f => ({ ...f, ...Object.fromEntries(Object.entries(p).filter(([, v]) => v != null)) })); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ownerType, ownerId]);

  const field = (key, label, opts = {}) => (
    <div className={opts.half ? 'flex-1 min-w-0' : ''}>
      <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>
        {label}{opts.required && <span className="text-red-500"> *</span>}
      </label>
      <input
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={opts.placeholder}
        className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
      />
    </div>
  );

  const submit = async () => {
    const requested = mode === 'full' ? owed : Number(amount);
    if (mode === 'custom' && (!Number.isFinite(requested) || requested <= 0)) {
      toast.error('Enter an amount'); return;
    }
    if (requested > owed) { toast.error(`You can request up to ${money(owed)}`); return; }
    if (!form.legalName.trim()) { toast.error('A legal name is required on an invoice'); return; }

    setSaving(true);
    try {
      // Saved every time rather than only when missing — details change, and
      // the invoice has to carry whatever is true today.
      await paymentsAPI.saveBillingProfile({ ownerType, ownerId, ...form });
      const req = await paymentsAPI.createPayoutRequest({
        ownerType, ownerId,
        ...(mode === 'full' ? { full: true } : { amount: requested }),
      });
      toast.success(`Invoice ${req.invoiceNumber} drafted for your approval`);
      onCreated?.(req);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Could not create the payout request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`${theme.card} rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-5 border-b ${theme.border}`}>
          <h3 className={`text-lg font-bold ${theme.text}`}>Request payout</h3>
          <button onClick={onClose} className={theme.textMuted}><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader size={20} className="animate-spin text-blue-500" /></div>
        ) : (
          <div className="p-5 space-y-5">
            <div>
              <p className={`text-sm ${theme.textSecondary} mb-3`}>
                Kynaix is holding <strong className={theme.text}>{money(owed)}</strong> for you.
              </p>
              <div className="space-y-2">
                {[
                  { id: 'full', label: `Everything — ${money(owed)}` },
                  { id: 'custom', label: 'A specific amount' },
                ].map(o => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setMode(o.id)}
                    aria-pressed={mode === o.id}
                    className={`w-full text-left px-3.5 py-3 rounded-xl border-2 text-sm font-medium transition ${
                      mode === o.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : `${theme.border} ${theme.cardHover}`
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {mode === 'custom' && (
                <input
                  type="number" min="0" max={owed} step="0.01" value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder={`Up to ${money(owed)}`}
                  className={`mt-2 w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} text-sm`}
                />
              )}
            </div>

            <div className={`pt-4 border-t ${theme.border}`}>
              <p className={`text-sm font-semibold ${theme.text}`}>Your billing details</p>
              <p className={`text-xs ${theme.textMuted} mb-3`}>
                {profile
                  ? 'Used on the invoice. Change them if anything is out of date.'
                  : 'An invoice has to name and address the supplier. Asked once, reused afterwards.'}
              </p>
              <div className="space-y-3">
                {field('legalName', 'Legal name', { required: true, placeholder: 'Your name or business name' })}
                {field('addressLine1', 'Address')}
                {field('addressLine2', 'Address line 2')}
                <div className="flex gap-3">
                  {field('city', 'City', { half: true })}
                  {field('region', 'State / region', { half: true })}
                </div>
                <div className="flex gap-3">
                  {field('postalCode', 'Postcode', { half: true })}
                  {field('country', 'Country', { half: true, placeholder: 'US' })}
                </div>
                {field('taxId', 'Tax ID', { placeholder: 'Leave blank if you are not registered' })}
              </div>
            </div>

            <div className={`flex items-start gap-2.5 p-3 rounded-lg ${theme.backgroundSecondary}`}>
              <FileText size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className={`text-xs ${theme.textSecondary} space-y-1`}>
                <p>
                  We'll draft the invoice from your transactions. Nothing is issued
                  until you've reviewed and confirmed it.
                </p>
                {/* Manual payouts take as long as a bank takes. Saying so up
                    front is the difference between waiting and chasing. */}
                <p>
                  Once confirmed, payment is sent by your usual payment method and
                  normally reaches you within <strong>1–2 weeks</strong>.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={onClose} className={`flex-1 px-4 py-2.5 border ${theme.border} ${theme.text} rounded-lg text-sm font-semibold`}>
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className={`flex-1 px-4 py-2.5 ${theme.buttonPrimary} rounded-lg text-sm font-semibold disabled:opacity-50`}
              >
                {saving ? 'Drafting…' : 'Draft invoice'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
